import React, { useEffect, useState } from "react";
// import OfferCard from "./offercard";
import { Inbox } from "lucide-react";

import { mapFormDataToApiPayload } from "./FormMapper";
import { useSearchParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import callApi from "../../../../utility/apiCaller";
import { useDispatch, useSelector } from "react-redux";
import { setSelectedOffer as setOfferToStore } from "../../../../store/app/aaSlice";
import OfferCard from "./offerCard2";
import Header from "../Header/header";
import useAddLead from "../../hooks/addLeadtoCRM";
import { setUserClickData } from "../../../../utility/setUserClickData";

// Respect the OS "reduce motion" setting for the load animation.
function usePrefersReducedMotion() {
  const [reduce, setReduce] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const on = () => setReduce(mq.matches);
    on();
    mq.addEventListener ? mq.addEventListener("change", on) : mq.addListener(on);
    return () => (mq.removeEventListener ? mq.removeEventListener("change", on) : mq.removeListener(on));
  }, []);
  return reduce;
}

// Each line/card rises + fades in, staggered by `delay` (ms).
function Reveal({ shown, delay = 0, reduce, className = "", children }) {
  const style = reduce
    ? undefined
    : {
        opacity: shown ? 1 : 0,
        transform: shown ? "none" : "translateY(20px)",
        transition:
          "opacity .6s cubic-bezier(.22,.7,.25,1), transform .6s cubic-bezier(.22,.7,.25,1)",
        transitionDelay: `${delay}ms`,
      };
  return (
    <div className={className} style={style}>
      {children}
    </div>
  );
}

// utils/emiCalculator.js
export function calculateEMI(principal, annualRate, months) {
  const r = annualRate / 12 / 100; // monthly interest rate
  if (r === 0) return Math.round(principal / months); // no interest case

  return Math.round(
    (principal * r * Math.pow(1 + r, months)) / (Math.pow(1 + r, months) - 1)
  );
}
async function withRetry(fn, retries = 2, delay = 2000) {
  let attempt = 0;
  while (attempt <= retries) {
    try {
      return await fn();
    } catch (err) {
      if (attempt === retries) throw err;
      console.warn(`Retrying... attempt ${attempt + 1} failed`);
      await new Promise((r) => setTimeout(r, delay * (attempt + 1)));
      attempt++;
    }
  }
}

const DMI_ID = "68ad8ed5078abf6c677e4303";
const DMI = "DMI";

// Lenders that must never be shown on the offer page
// (neither in the Success list nor in the Failed list).
// IIFL / Muthoot / Manipal are now shown on the offers page (unhidden).
const HIDDEN_LENDER_IDS = ["6ac66fc2f94ba4d620c51765"];
const HIDDEN_LENDER_NAME_REGEX = /^$/; // matches nothing real -> hides no lender

// In the failed list, show a clean display name instead of the internal one.
// e.g. "Prefr Preapproved" -> "Prefr"
const toFailedDisplayName = (name) =>
  /prefr/i.test(String(name ?? "")) ? "Prefr" : name;

// Wrapper to use offer object
export function getOfferEMI(offer, amount = null, tenor = null) {
  const principal = amount || offer.max_amount; // default use max loan amount
  const months = tenor || offer.max_tenor; // default use max tenor
  const roi = offer.roi;

  return calculateEMI(principal, roi, months);
}

export default function LoanOfferPage({
  setCurrentStep,
  formdata,
  applicationId,
  responseId,
  // leadId,
  MappedData,
  aaUrl,
  internalLeadId,
  setSelectedOffer,
}) {
  const offersData = useSelector((state) => state.app.allOffers);
  const [params] = useSearchParams();
  const dmioffersData = useSelector((state) => state.app.offers);
  const navigate = useNavigate();
  const user = useSelector((state) => state.app.user);
  console.log("offers: ", offersData);
  const aaSlice = useSelector((state) => state?.aa);
  let leadId = aaSlice?.internalLeadId;
  const dispatch = useDispatch();
  const [expandedId, setExpandedId] = useState(null);
  const [failedList, setFailedList] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const addLead = useAddLead();
  // Flatten all offers
  const allOffers = Array.isArray(offersData)
    ? offersData.flatMap((item) => item.offers || [])
    : offersData?.offers
    ? offersData.offers
    : [];

  // Remove the hidden lenders from both the Success and Failed lists
  // before anything is rendered on this page.
  const visibleOffers = (offersData?.offers || [])
    .filter((offer) => !HIDDEN_LENDER_IDS.includes(offer?.lender_id))
    // show by priority (1 first); missing priority sorts last. (.filter makes a
    // new array, so .sort here does not mutate the redux offers.)
    .sort((a, b) => (a?.priority ?? 999) - (b?.priority ?? 999));
  // Names of hidden lenders (taken from any offer / failed entry that carries
  // the hidden lender_id) so plain-string failed entries can be hidden too.
  const hiddenLenderNames = [
    ...(offersData?.offers || []),
    ...(offersData?.failedList || []),
  ]
    .filter(
      (item) =>
        item &&
        typeof item === "object" &&
        HIDDEN_LENDER_IDS.includes(String(item.lender_id ?? ""))
    )
    .map((item) => String(item.lender_name ?? "").trim().toLowerCase())
    .filter(Boolean);
  // Failed + duplicate entries, without the hidden lenders.
  const visibleFailedList = (offersData?.failedList || []).filter((item) =>
    item && typeof item === "object"
      ? !HIDDEN_LENDER_IDS.includes(String(item.lender_id ?? ""))
      : !hiddenLenderNames.includes(String(item ?? "").trim().toLowerCase()) &&
        !HIDDEN_LENDER_NAME_REGEX.test(String(item ?? ""))
  );

  useEffect(() => {
    setExpandedId(visibleOffers?.[0]?._id);
  }, []);

  // Find highest max_amount offer
  const highestAmountOffer =
    allOffers.length > 0
      ? allOffers.reduce((prev, current) =>
          current.max_amount > prev.max_amount ? current : prev
        )
      : null;

  const handleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const handleShowMoreOffers = () => {
    setUserClickData({
      event_name: `show-more-offers-multistep-pl-v1`,
      user_id:
        formdata?.mobile ||
        visibleOffers?.[0]?.contact_phone ||
        leadId ||
        "unknown",
      affiliate_id: params.get("aff_id") || "No Aff_id found",
      utm_source: params.get("utm_source") || params.get("utm_medium"),
      utm_term: params.get("utm_term"),
    });
    setShowAll(true);
  };

  //console.log("highestAmountOffer:", highestAmountOffer);

  // State to control iframe visibility
  const [showIframe, setShowIframe] = useState(false);

  // Load animation: flip `shown` on after mount so the reveal transition runs.
  const reduceMotion = usePrefersReducedMotion();
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(id);
  }, []);
  const handleClickNext = async () => {
    if (!highestAmountOffer) return;

    try {
      // 1. Prepare the payload
      const payload = {
        lead: {
          dmi_offer: {
            tenure: highestAmountOffer.max_tenor,
            interest: highestAmountOffer.roi,
            amount: highestAmountOffer.max_amount,
          },
        },
      };

      // 2. Call the API using leadId from props
      await callApi(
        `v1/lead/${leadId}/update-lead-new`,
        "post",
        payload,
        "core", // Assuming "loan" sets the base URL
        user?.token ||
          "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJfaWQiOiI2OTAxYjljZjIxZTZlNTJmYTQwNDAwNDkiLCJvdHAiOiIxMTExIiwiY29udGFjdF9waG9uZSI6Ijk2OTMxMzU0NjYiLCJvdHBfZXhwaXJ5IjoiMjAyNS0xMC0yOVQwNzowMzowMy43MDZaIiwiaWF0IjoxNzYxNzIwNzg3fQ.xJ3Qd29aQPzV6vfc-bm5XDaOyIF1dXI4dD3lRZxEBDo"
      );

      setSelectedOffer(payload.lead.dmi_offer);
      dispatch(setOfferToStore(payload.lead.dmi_offer));
      const updateResponse = await withRetry(() =>
        callApi(
          `v1/lead/${leadId}/update-lead-new`,
          "post",
          {
            lead: {
              is_claim_offer_click_completed: true,
              steps_completed: 6,
            },
          },
          "core",
          user?.token ||
            "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJfaWQiOiI2OTAxYjljZjIxZTZlNTJmYTQwNDAwNDkiLCJvdHAiOiIxMTExIiwiY29udGFjdF9waG9uZSI6Ijk2OTMxMzU0NjYiLCJvdHBfZXhwaXJ5IjoiMjAyNS0xMC0yOVQwNzowMzowMy43MDZaIiwiaWF0IjoxNzYxNzIwNzg3fQ.xJ3Qd29aQPzV6vfc-bm5XDaOyIF1dXI4dD3lRZxEBDo"
        )
      );
      //console.log(updateResponse);

      // Call useAddLead hook when offer is selected
      if (leadId) {
        try {
          await addLead({
            leadId: leadId,
            lead_type: "live",
            priority: 2,
            stage: "Offer",
          });
        } catch (error) {
          console.error("Error adding lead to CRM:", error);
        }
      }

      // 3. Move to next step (show iframe or next UI)
      setCurrentStep((prev) => prev + 1);
    } catch (error) {
      console.error("Error updating lead:", error);
    }
  };

  return visibleOffers.length === 0 ? (
    <div className="dm-offers-v9 max-w-[400px] mx-auto bg-white min-h-screen flex flex-col relative overflow-hidden px-4 pb-7">
      {/* Logo */}
      <div className="flex justify-center w-full items-center pt-6 pb-2">
        <img src="/assets/img/newLogo.png" alt="digitmoney logo" className="h-auto w-[140px]" />
      </div>

      {/* Empty state */}
      <div className="bg-white rounded-[24px] border border-[#ededed] mt-[10dvh] px-6 py-8 text-center w-full">
        <Inbox className="mx-auto mb-4 h-[80px] w-[80px] text-[#12f280]" />
        <h2 className="text-[28px] text-[#1b1b1b] font-atkinson font-bold mb-2">Oops!</h2>
        <p className="text-[16px] text-[#8b8b8b] leading-[22px] font-atkinson mb-6">
          We currently have no pre-approved offers available for you. <br />
          Please check back later.
        </p>
        <p className="text-[#006f59] text-[16px] font-atkinson font-bold">
          Stay connected for exciting offers!
        </p>
      </div>

      <p className="text-center text-[#b3b3b3] text-[12px] leading-[14px] mt-6 px-4 font-atkinson">
        *These offers are subject to change at the discretion of the Bank / NBFC. Please ensure your
        details are updated to receive the latest offers.
      </p>
    </div>
  ) : (
    <div className="dm-offers-v9 max-w-[400px] mx-auto bg-white min-h-screen px-4 pb-[160px]">
      {/* Back */}
      <div className="flex items-center pt-6 pb-1">
        <button
          type="button"
          onClick={() => setCurrentStep(3)}
          aria-label="Go back"
          className="w-6 h-6 flex-none flex items-center justify-center"
        >
          <img src={process.env.PUBLIC_URL + "/assets/icons/left_arrow.png"} alt="" className="w-5 h-5 object-contain" />
        </button>
        <div className="flex-1">
          <Reveal shown={shown} reduce={reduceMotion} delay={0}>
            <h2 className="text-center font-atkinson font-bold text-[22px] tracking-tight text-[#1b1b1b]">
              Congratulations 🎉
            </h2>
          </Reveal>
        </div>
        <span className="w-6 flex-none" aria-hidden="true" />
      </div>

      <Reveal shown={shown} reduce={reduceMotion} delay={110}>
        <p className="text-center font-atkinson text-[13px] leading-[1.35] mt-[10px] text-[#1b1b1b]">
          <span className="text-[#ff7a1a] font-bold">{visibleOffers.length} lenders</span> matched your profile.
        </p>
      </Reveal>

      <Reveal shown={shown} reduce={reduceMotion} delay={160}>
        <p className="text-center font-atkinson text-[11px] leading-[1.4] mt-[6px] text-[#8b8b8b]">
          You're one step away from funding your{" "}
          <b className="text-[#1b1b1b]">“Purpose of Loan”</b>.
        </p>
      </Reveal>

      <Reveal shown={shown} reduce={reduceMotion} delay={220}>
        <div className="flex items-center justify-center gap-2 bg-[#d7f7e6] text-[#0b7a55] font-atkinson text-[11px] leading-[1.25] rounded-full px-4 py-3 mt-[18px]">
          ⏱ Most customers complete the next step in under 3 minutes.
        </div>
      </Reveal>

      {/* Offer cards (staggered in) */}
      <div>
        {(showAll ? visibleOffers : visibleOffers.slice(0, 3)).map((offer, i) => (
          <Reveal key={offer._id} shown={shown} reduce={reduceMotion} delay={340 + i * 130}>
            <OfferCard
              setCurrentStep={setCurrentStep}
              setSelectedOffer={setSelectedOffer}
              leadId={leadId}
              offer={offer}
              isExpanded={expandedId === offer._id}
              onExpand={() => handleExpand(offer._id)}
            />
          </Reveal>
        ))}
      </div>

      {!showAll && visibleOffers.length > 3 && (
        <button
          type="button"
          onClick={handleShowMoreOffers}
          className="w-full mt-4 border-[1.5px] border-[#1b1b1b] text-[#1b1b1b] font-atkinson font-bold text-[16px] rounded-[16px] py-[15px] active:scale-[0.99] transition-transform"
        >
          Show More Offers
        </button>
      )}

      {/* Failed lenders -> note line + same card design (N/A, action disabled) */}
      {visibleFailedList.length > 0 && (
        <p className="font-atkinson text-[12px] text-[#8b8b8b] mt-6 mt-3 text-center">
          Note: There are no matching offers for your request from
        </p>
      )}
      {visibleFailedList.map((f, i) => (
        <OfferCard
          key={"failed-" + i}
          offer={f}
          failed={true}
          setCurrentStep={setCurrentStep}
          setSelectedOffer={setSelectedOffer}
        />
      ))}

      {/* Disclaimer — sticky footer */}
      <div className="fixed bottom-0 left-0 right-0 z-40">
        <div className="max-w-[400px] mx-auto bg-white px-4 pt-3 pb-4 border-t border-[#f0f0f0]">
          <p className="text-left text-[#b3b3b3] text-[12px] leading-[14px] font-atkinson">
            <b>Discliamer:</b> These pre-approved offers are subject to change at discretion of Bank / NBFC after receiving
            all your documents and details. Final offer will be based on risk policy of Bank / NBFC. We do
            not guarantee that final offer will be same as Pre-approved offer.
            <br/><br />
            Note: You may receive calls from multiple lenders.
          </p>
        </div>
      </div>
    </div>
  );
}